import React, { useState } from 'react'
import styles from './BookingSection.module.css'
import { Step1Details } from './Step1Details'
import { Step2Otp } from './Step2Otp'
import { Step3Success } from './Step3Success'
import { TrustFeatures } from './TrustFeatures'
import { BookingStep, AppointmentDetails } from '@/types/appointment'
import { sendOtp, resendOtp, verifyAndBook } from '@/services/appointmentService'

export const BookingSection: React.FC = () => {
  const [step, setStep] = useState<BookingStep>('details')
  const [name, setName] = useState('')
  const [mobileNumber, setMobileNumber] = useState('')
  const [sessionId, setSessionId] = useState('')
  const [appointment, setAppointment] = useState<AppointmentDetails | null>(null)

  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // ── Step 1: Send OTP ──
  const handleSendOtp = async (inputName: string, inputMobile: string) => {
    setName(inputName)
    setMobileNumber(inputMobile)
    setIsLoading(true)
    setErrorMessage(null)

    try {
      const response = await sendOtp({
        name: inputName,
        mobileNumber: inputMobile,
      })

      if (response.success) {
        setSessionId(response.sessionId || '')
        if (response.mockOtp) {
          setSuccessMessage(`[MOCK MODE] Your OTP is: ${response.mockOtp}`)
        }
        setStep('otp')
      } else {
        setErrorMessage(response.message || 'Unable to send OTP. Please try again.')
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to connect to verification service'
      setErrorMessage(msg)
    } finally {
      setIsLoading(false)
    }
  }

  // ── Step 2: Resend OTP ──
  const handleResendOtp = async () => {
    setIsResending(true)
    setErrorMessage(null)
    setSuccessMessage(null)

    try {
      const response = await resendOtp({
        name,
        mobileNumber,
      })

      if (response.success) {
        if (response.mockOtp) {
          setSuccessMessage(`[MOCK MODE] Your new OTP is: ${response.mockOtp}`)
        } else {
          setSuccessMessage('A new verification code has been dispatched to your mobile.')
        }
        setTimeout(() => setSuccessMessage(null), 8000)
      } else {
        setErrorMessage(response.message || 'Unable to resend OTP at this moment.')
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to resend verification code'
      setErrorMessage(msg)
    } finally {
      setIsResending(false)
    }
  }

  // ── Step 2: Verify & Book Appointment ──
  const handleVerifyAndBook = async (otp: string, consentGiven: boolean) => {
    setIsLoading(true)
    setErrorMessage(null)

    try {
      const response = await verifyAndBook({
        name,
        mobileNumber,
        otp,
        consentGiven,
        sessionId,
      })

      if (response.success && response.appointment) {
        setAppointment(response.appointment)
        setStep('success')
      } else {
        setErrorMessage(
          response.message || 'Verification could not be completed. Please verify your OTP.'
        )
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'An error occurred during appointment confirmation'
      setErrorMessage(msg)
    } finally {
      setIsLoading(false)
    }
  }

  // ── Return to Step 1 to edit info ──
  const handleEditDetails = () => {
    setErrorMessage(null)
    setSuccessMessage(null)
    setStep('details')
  }

  // ── Reset entire form ──
  const handleBookAnother = () => {
    setName('')
    setMobileNumber('')
    setSessionId('')
    setAppointment(null)
    setErrorMessage(null)
    setSuccessMessage(null)
    setStep('details')
  }

  return (
    <section className={styles.bookingSection} id="book" aria-label="Book Diagnostic Appointment">
      <div className={styles.container}>
        {/* Step Progression Breadcrumb */}
        <div className={styles.stepperContainer} aria-label="Booking Progress">
          <div
            className={`${styles.stepIndicator} ${
              step === 'details' ? styles.stepActive : styles.stepCompleted
            }`}
          >
            <span className={styles.stepNum}>{step === 'details' ? '1' : '✓'}</span>
            <span className={styles.stepLabel}>Patient Details</span>
          </div>

          <div className={styles.stepDivider} aria-hidden="true" />

          <div
            className={`${styles.stepIndicator} ${
              step === 'otp'
                ? styles.stepActive
                : step === 'success'
                ? styles.stepCompleted
                : ''
            }`}
          >
            <span className={styles.stepNum}>{step === 'success' ? '✓' : '2'}</span>
            <span className={styles.stepLabel}>OTP Verification</span>
          </div>

          <div className={styles.stepDivider} aria-hidden="true" />

          <div
            className={`${styles.stepIndicator} ${
              step === 'success' ? styles.stepActive : ''
            }`}
          >
            <span className={styles.stepNum}>3</span>
            <span className={styles.stepLabel}>Confirmed</span>
          </div>
        </div>

        {/* Main Grid: Form + Trust Features Sidebar */}
        <div className={styles.bookingGrid}>
          <div className={styles.formCol}>
            {step === 'details' && (
              <Step1Details
                initialName={name}
                initialMobile={mobileNumber}
                isLoading={isLoading}
                errorMessage={errorMessage}
                onSubmit={handleSendOtp}
                onClearError={() => setErrorMessage(null)}
              />
            )}

            {step === 'otp' && (
              <Step2Otp
                name={name}
                mobileNumber={mobileNumber}
                isLoading={isLoading}
                isResending={isResending}
                errorMessage={errorMessage}
                successMessage={successMessage}
                onVerifyAndBook={handleVerifyAndBook}
                onResendOtp={handleResendOtp}
                onEditDetails={handleEditDetails}
                onClearError={() => setErrorMessage(null)}
              />
            )}

            {step === 'success' && appointment && (
              <Step3Success
                appointment={appointment}
                onBookAnother={handleBookAnother}
              />
            )}
          </div>

          {/* Side Trust Column */}
          <div className={styles.sidebarCol}>
            <TrustFeatures />
          </div>
        </div>
      </div>
    </section>
  )
}
export default BookingSection

